import { useState } from 'react'
import { MessagesLandingMenuItem } from './types'
import { deepCopy } from './utils'
import { toast } from 'react-toastify'
import { createRestClient } from '@/contexts/QueryProvider'
import { useGetSearchParam } from '@/hooks'
import { useQueryClient } from '@tanstack/react-query'

export function useMenuOperations(initialData: MessagesLandingMenuItem[]) {
  const appId = useGetSearchParam<number>('appId')
  const queryClient = useQueryClient()
  const { postUiApiUpdateMenu, getUiApiGetMenu, getLandingApiGetPlatformOperations } = createRestClient()

  // Track expanded item IDs
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  // Store a mutable copy of the data
  const [menuItems, setMenuItems] = useState<MessagesLandingMenuItem[]>(() => initialData)
  const hasChanged = JSON.stringify(initialData) !== JSON.stringify(menuItems)
  // Track whether top-level items are expanded
  const [isExpanded, setIsExpanded] = useState(false)

  const { mutate: saveMenu } = postUiApiUpdateMenu.useMutation()

  const invalidateOnSuccess = () => {
    getUiApiGetMenu.invalidateQueries({ parameters: { path: { applicationId: appId, grouping: 'menu' } } }, queryClient)
    getLandingApiGetPlatformOperations.invalidateQueries({}, queryClient)
  }

  const handleSave = () => {
    if (!appId) return
    saveMenu(
      {
        path: { applicationId: appId, grouping: 'menu' },
        body: { definition: JSON.stringify({ items: menuItems }) },
      },
      {
        onSuccess: () => {
          toast.success('Menu saved successfully!')
          invalidateOnSuccess()
        },
        onError: (error: unknown) => {
          console.error('Error saving menu:', error)
          toast.error('Failed to save the menu. Please try again.')
        },
      }
    )
  }

  // Toggle expanded state
  const toggleItem = (id: string) => {
    if (expandedItems.includes(id)) {
      setExpandedItems(expandedItems.filter((item) => item !== id))
    } else {
      setExpandedItems([...expandedItems, id])
    }
  }

  // Collapse all
  const collapseAll = () => {
    setExpandedItems([])
    setIsExpanded(false)
  }

  // Expand top level
  const expandTopLevel = () => {
    const topLevelIds = menuItems.map((_, index) => `${index}`)
    setExpandedItems(topLevelIds)
    setIsExpanded(true)
  }

  // Toggle between expand and collapse
  const toggleExpandCollapse = () => {
    if (isExpanded) {
      collapseAll()
    } else {
      expandTopLevel()
    }
  }

  // Move an item up
  const moveItemUp = (id: string) => {
    // Parse the ID to get the path to the item
    const path = id.split('-').map(Number)

    // Create a deep copy of the data
    const newData = deepCopy(menuItems)

    // If it's a top-level item
    if (path.length === 1) {
      const index = path[0]
      if (index > 0) {
        // Swap with the previous item
        ;[newData[index], newData[index - 1]] = [newData[index - 1], newData[index]]
        setMenuItems(newData)
      }
      return
    }

    // For nested items, we need to find the parent array
    let currentItems = newData

    // Navigate to the parent array
    for (let i = 0; i < path.length - 1; i++) {
      const index = path[i]
      if (currentItems[index] && currentItems[index].items) {
        currentItems = currentItems[index].items
      } else {
        // If we can't find the item, just return
        return
      }
    }

    // Get the index in the parent array
    const index = path[path.length - 1]
    if (index > 0) {
      // Swap with the previous item
      ;[currentItems[index], currentItems[index - 1]] = [currentItems[index - 1], currentItems[index]]
      setMenuItems(newData)
    }
  }

  // Move an item down
  const moveItemDown = (id: string) => {
    // Parse the ID to get the path to the item
    const path = id.split('-').map(Number)

    // Create a deep copy of the data
    const newData = deepCopy(menuItems)

    // If it's a top-level item
    if (path.length === 1) {
      const index = path[0]
      if (index < newData.length - 1) {
        // Swap with the next item
        ;[newData[index], newData[index + 1]] = [newData[index + 1], newData[index]]
        setMenuItems(newData)
      }
      return
    }

    // For nested items, we need to find the parent array
    let currentItems = newData

    // Navigate to the parent array
    for (let i = 0; i < path.length - 1; i++) {
      const index = path[i]
      if (currentItems[index] && currentItems[index].items) {
        currentItems = currentItems[index].items
      } else {
        // If we can't find the item, just return
        return
      }
    }

    // Get the index in the parent array
    const index = path[path.length - 1]
    if (index < currentItems.length - 1) {
      // Swap with the next item
      ;[currentItems[index], currentItems[index + 1]] = [currentItems[index + 1], currentItems[index]]
      setMenuItems(newData)
    }
  }

  // Add a child menu item to a parent item
  const addChildItem = (id: string, newItem: MessagesLandingMenuItem) => {
    // Parse the ID to get the path to the parent item
    const path = id.split('-').map(Number)

    // Create a deep copy of the data
    const newData = deepCopy(menuItems)

    // Find the parent item
    let targetItem: MessagesLandingMenuItem | null = null

    // If it's a top-level item
    if (path.length === 1) {
      const index = path[0]
      if (index >= 0 && index < newData.length) {
        targetItem = newData[index]
      }
    } else {
      // For nested items, navigate to the parent item
      let currentItems = newData

      for (let i = 0; i < path.length; i++) {
        const index = path[i]
        if (currentItems[index]) {
          if (i === path.length - 1) {
            // This is the target item
            targetItem = currentItems[index]
          } else if (currentItems[index].items) {
            // Continue navigating
            currentItems = currentItems[index].items
          } else {
            // If we can't find the item, just return
            return
          }
        } else {
          // Invalid index
          return
        }
      }
    }

    // If we found the target item, add the new child
    if (targetItem) {
      // Initialize items array if it doesn't exist
      if (!targetItem.items) {
        targetItem.items = []
      }

      // Add the new item to the end of the items array
      targetItem.items.push(newItem)

      // Expand the parent to show the new child
      if (!expandedItems.includes(id)) {
        setExpandedItems([...expandedItems, id])
      }

      // Update the data
      setMenuItems(newData)
    }
  }

  // Delete a menu item
  const deleteItem = (id: string) => {
    // Parse the ID to get the path to the item
    const path = id.split('-').map(Number)

    // Create a deep copy of the data
    const newData = deepCopy(menuItems)

    // If it's a top-level item
    if (path.length === 1) {
      const index = path[0]
      if (index >= 0 && index < newData.length) {
        // Remove the item from the top-level array
        newData.splice(index, 1)
        setMenuItems(newData)
      }
      return
    }

    // For nested items, we need to find the parent array
    let currentItems = newData

    // Navigate to the parent array
    for (let i = 0; i < path.length - 1; i++) {
      const index = path[i]
      if (currentItems[index] && currentItems[index].items) {
        currentItems = currentItems[index].items
      } else {
        // If we can't find the item, just return
        return
      }
    }

    // Get the index in the parent array
    const index = path[path.length - 1]
    if (index >= 0 && index < currentItems.length) {
      // Remove the item from the parent array
      currentItems.splice(index, 1)

      // If this item was expanded, remove it from expandedItems
      if (expandedItems.includes(id)) {
        setExpandedItems(expandedItems.filter((itemId) => itemId !== id && !itemId.startsWith(`${id}-`)))
      }

      // Update the data
      setMenuItems(newData)
    }
  }

  // Edit a menu item
  const editItem = (id: string, updatedItem: Partial<MessagesLandingMenuItem>) => {
    // Parse the ID to get the path to the item
    const path = id.split('-').map(Number)

    // Create a deep copy of the data
    const newData = deepCopy(menuItems)

    // Find the target item
    let targetItem: MessagesLandingMenuItem | null = null

    // If it's a top-level item
    if (path.length === 1) {
      const index = path[0]
      if (index >= 0 && index < newData.length) {
        targetItem = newData[index]
      }
    } else {
      // For nested items, navigate to find the item
      let currentItems = newData

      for (let i = 0; i < path.length; i++) {
        const index = path[i]
        if (i === path.length - 1) {
          // This is the target item
          if (index >= 0 && index < currentItems.length) {
            targetItem = currentItems[index]
          }
        } else if (currentItems[index] && currentItems[index].items) {
          // Continue navigating
          currentItems = currentItems[index].items
        } else {
          // If we can't find the item, just return
          return
        }
      }
    }

    // If we found the target item, update its properties
    if (targetItem) {
      // Update each property that was provided
      Object.keys(updatedItem).forEach((key) => {
        const typedKey = key as keyof MessagesLandingMenuItem
        if (updatedItem[typedKey] !== undefined) {
          // Preserve the items array to maintain the hierarchy
          if (typedKey !== 'items') {
            // Use type assertion to avoid 'any' type
            ;(targetItem as Record<keyof MessagesLandingMenuItem, unknown>)[typedKey] = updatedItem[typedKey]
          }
        }
      })

      // Update the data
      setMenuItems(newData)
    }
  }

  // Add a new root level menu item
  const addRootItem = (newItem: MessagesLandingMenuItem) => {
    // Create a deep copy of the data
    const newData = deepCopy(menuItems)

    // Add the new item to the end of the root level array
    newData.push(newItem)

    // Update the data
    setMenuItems(newData)
  }

  return {
    menuItems,
    setMenuItems,
    expandedItems,
    isExpanded,
    hasChanged,
    initialData,
    toggleItem,
    collapseAll,
    expandTopLevel,
    toggleExpandCollapse,
    moveItemUp,
    moveItemDown,
    addChildItem,
    deleteItem,
    editItem,
    addRootItem,
    handleSave,
  }
}
